import React, {useContext, useEffect, useState} from "react";
import {AiTierServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table} from "antd";
import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useNavigate} from "react-router-dom";
import * as globalStyles from "../App.module.css";
import AiTier from "../../domain/AiTier";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import * as styles from "./AiTierList.module.css";
import CreditsValue from "../common/CreditsValue";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

function AiTierList() {
    const appContext = useContext(AppContextContext);
    const aiTierService = useContext(AiTierServiceContext);
    const navigate = useNavigate();
    const [tableStateId, setTableStateId] = useState(crypto.randomUUID());

    const title = "AI tiers";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button type={"primary"} icon={<PlusOutlined/>} onClick={() => navigate("/definitions/ai-tiers/add")}>
                        Add
                    </Button>
                </div>

                <AiTierTable key={tableStateId} columns={getColumns()}/>
            </>
        </DocumentTitle>
    );

    function getColumns(): ColumnsType<AiTier> {
        return [
            {
                dataIndex: "name",
                title: "Name",
                sorter: true,
                sortDirections: ascendingFirstSortDirections,
                defaultSortOrder: "ascend",
                render: (value, aiTier: AiTier) =>
                    <Link to={`/definitions/ai-tiers/${aiTier.id}`}>{value}</Link>
            },
            {
                dataIndex: "maxCreditsPerMonth",
                title: "Max credits / month",
                align: "right",
                render: value => <CreditsValue value={value} compact={true} showIcon={false}/>
            },
            {
                title: "Actions",
                width: 100,
                className: globalStyles["table-actions"],
                render: renderAction
            }
        ];
    }

    function renderAction(value: any, aiTier: AiTier) {
        return (
            <Space>
                <Button
                    icon={<DeleteFilled/>}
                    className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete AI tier <b>{aiTier.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(aiTier)
                        });
                    }}
                />
            </Space>
        );
    }

    function onDeleteConfirm(aiTier: AiTier) {
        aiTierService.delete(aiTier)
            .then(() => {
                message.success("AI tier deleted.");
                setTableStateId(crypto.randomUUID());
            });
    }
}

export default AiTierList;

interface TableProps {
    columns: ColumnsType<AiTier>;
}

function AiTierTable(props: TableProps) {
    const aiTierService = useContext(AiTierServiceContext);
    const [aiTiers, setAiTiers] = useState<PagedResult<AiTier>>();

    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: "AiTierList",
    });

    useEffect(() => tableHandler.reload(), []);

    return (
        <>
            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
            </div>

            <Table
                dataSource={aiTiers?.data}
                loading={tableHandler.loading}
                pagination={tableHandler.pagination}
                showSorterTooltip={false}
                rowKey="id"
                onChange={tableHandler.onTableChange}
                columns={props.columns}
                className={styles.table}
            />
        </>
    );

    function reload() {
        return aiTierService.getList(tableHandler.queryOptions)
            .then(result => {
                tableHandler.updateTotal(result.total);
                setAiTiers(result);
            });
    }
}
